"use client";

import React, { useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Scrollbar, Navigation, Pagination, Keyboard } from "swiper/modules";
import { motion } from "framer-motion";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

import "./Gallery.css";
import Image from "next/image";

export default function Gallery({ images = [] }) {
  // 🎯 Motion
  const slideVariants = {
    hidden: {
      opacity: 0,
      y: 60,
    },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.12,
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1],
      },
    }),
  };

  // ✅ Filter images
  const sampleImages = images.filter((img) =>
    img?.title?.toLowerCase().includes("sample"),
  );

  const threeDImages = images.filter(
    (img) => img?.title?.toLowerCase() === "3d",
  );

  // ✅ Availability flags
  const hasSample = sampleImages.length > 0;
  const has3D = threeDImages.length > 0;

  // ✅ Default tab (smart)
  const defaultTab = hasSample ? "sample" : "3d";
  const [activeTab, setActiveTab] = useState(defaultTab);

  // ✅ Decide active images
  const activeImages =
    hasSample && has3D
      ? activeTab === "3d"
        ? threeDImages
        : sampleImages
      : hasSample
        ? sampleImages
        : threeDImages;

  // ✅ Image URL helper (safe)
  const getImageUrl = (url) => {
    if (!url) return "";
    return url.includes("?") ? `${url}&h=614&q=100` : `${url}&h=614&q=100`;
  };

  // ❌ No images → don't render section
  if (!hasSample && !has3D) return null;

  return (
    <section className="section-padding">
      <motion.div
        className="fourth-container inner-flex inner-flex-medium"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{
          duration: 0.9,
          ease: [0.22, 1, 0.36, 1],
        }}
      >
        <div className="main-container">
          <div className="flex-row alc j-c-sb gallerytab">
            <div className="fourth-title-text">
              <h3 className="uppercases">Gallery</h3>
            </div>

            {/* ✅ Show tabs ONLY if both exist */}
            {hasSample && has3D && (
              <div className="tab-bttn">
                <div className="base-TabsList-root base-TabsList-horizontal tabs-list flex-row">
                  <button
                    type="button"
                    onClick={() => setActiveTab("sample")}
                    className={`base-Tab-root tab-button ${
                      activeTab === "sample" ? "active" : ""
                    }`}
                  >
                    Sample House
                  </button>

                  <button
                    type="button"
                    onClick={() => setActiveTab("3d")}
                    className={`base-Tab-root tab-button ${
                      activeTab === "3d" ? "active" : ""
                    }`}
                  >
                    3D
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ✅ Swiper */}
        <div className="relative">
          <Swiper
            pagination={{
              clickable: true,
              dynamicBullets: true,
              el: ".swiper-pagination.swiper-pagination-gallery",
            }}
            spaceBetween={20}
            slidesPerView={2}
            breakpoints={{
              0: { slidesPerView: 1 },
              768: { slidesPerView: 1.2 },
              1024: { slidesPerView: 2 },
            }}
            navigation={{
              nextEl: ".swiper-button-next.gallery",
              prevEl: ".swiper-button-prev.gallery",
            }}
            keyboard={{
              enabled: true,
              onlyInViewport: true,
            }}
            modules={[Navigation, Scrollbar, Pagination, Keyboard]}
            className="fourth-image-gallery-swiper horizontal relative"
          >
            {activeImages?.map((img, index) => (
              <SwiperSlide key={index}>
                <motion.div
                  className="gallery-img-wrapper"
                  custom={index}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.15 }}
                  variants={slideVariants}
                >
                  <a href={img.image_full} data-fancybox="gallery">
                    <div
                      className="relative gallery-img"
                      style={{
                        aspectRatio: "950 / 614",
                        background: "#dedede",
                      }}
                    >
                      <Image
                        src={getImageUrl(img.image)}
                        fill
                        loading="lazy"
                        alt={`gallery-${index}`}
                      />
                    </div>
                  </a>
                </motion.div>
              </SwiperSlide>
            ))}

            {/* Navigation */}
            <div className="gallery-swiper-white-box-btn hide-mobile-only">
              <div className="swiper-button-prev gallery">
                <img src="/images/icon/swiper-left.svg" alt="" />
              </div>
              <div className="swiper-button-next gallery">
                <img src="/images/icon/swiper-right.svg" alt="" />
              </div>
            </div>
          </Swiper>
        </div>

        {/* Pagination */}
        <div className="visible-tab-mobile">
          <div className="swiper-pagination swiper-pagination-gallery"></div>
        </div>

        {/* View All */}
        <div className="main-container">
          {activeImages?.length > 0 && (
            <a
              href={activeImages[0]?.image_full}
              data-fancybox="project-gallery-fancy4"
            >
              <button className="reecosys-template-button button-style-primary-outline m0auto">
                <span className="material-symbols-outlined">photo_library</span>
                <p>View All Images</p>
              </button>
            </a>
          )}
        </div>
      </motion.div>
    </section>
  );
}
